/**
 * Calendário real (SPEC-20261027-2350-calendario-real) — a lógica pura da tela.
 *
 * Sem React e sem `lib/api.ts`: datas da grade, período pedido à API, eventos
 * de vários dias, posição na grade de horas, filtros do explorer e o
 * formulário do evento. Tudo testado em `calendario.test.ts`.
 *
 * Dia é sempre a chave `AAAA-MM-DD` (o formato da API): a conta é feita em UTC
 * sobre a chave, para que horário de verão nunca pule nem repita um dia.
 */

/* ------------------------------------------------------------- contrato -- */

/** Agendas em que se cria evento (as do protótipo). */
export type AgendaDeEvento = 'pessoal' | 'empresa' | 'datas'
/**
 * + as agendas DERIVADAS, que não aceitam evento à mão: Itens de Trabalho e
 * o Google Calendar da pessoa (SPEC-20261027-2350-integracao-google-calendar).
 */
export type AgendaId = AgendaDeEvento | 'itens' | 'google'

/** `GET /calendar/events` → `eventos[]`. */
export type EventoDaApi = {
  id: string
  agenda: AgendaDeEvento
  title: string
  description: string
  location: string
  meetingUrl: string | null
  date: string
  endDate: string | null
  startTime: string | null
  endTime: string | null
  ownerMemberId: string
  ownerName: string
  memberIds: string[]
  podeEditar: boolean
  /** Vai também para o Google Calendar de quem criou. */
  googleSync?: boolean
  /** Link da cópia no Google — só para quem criou, quando ela está lá. */
  googleLink?: string | null
}

/** `GET /calendar/events` → `google[]`: evento do Google da pessoa (somente leitura). */
export type GoogleDaApi = {
  id: string
  title: string
  description: string
  location: string
  meetingUrl: string | null
  htmlLink: string | null
  date: string
  endDate: string | null
  startTime: string | null
  endTime: string | null
}

/** `GET /calendar/events` → `itens[]`. */
export type ItemDaApi = {
  id: string
  code: string
  title: string
  startDate: string | null
  dueDate: string | null
  assigneeMemberId: string | null
  concluido: boolean
}

/** Corpo de `POST` e `PUT /calendar/events`. */
export type CorpoDoEvento = {
  agenda: AgendaDeEvento
  title: string
  description: string
  location: string
  meetingUrl: string
  date: string
  endDate: string | null
  startTime: string | null
  endTime: string | null
  memberIds: string[]
  googleSync: boolean
}

/**
 * O que a tela desenha: um evento da API ou um marco (início/entrega) de um
 * Item de Trabalho. `id` é único entre os dois (o item gera dois).
 */
export type Compromisso = {
  id: string
  agenda: AgendaId
  title: string
  date: string
  endDate: string | null
  startTime: string | null
  endTime: string | null
  location: string
  /** Dono do evento; nos itens, o responsável. */
  ownerMemberId: string | null
  memberIds: readonly string[]
  evento: EventoDaApi | null
  item: (ItemDaApi & { marco: 'inicio' | 'entrega' }) | null
  /** Evento do Google Calendar da pessoa — abre no Google. */
  google?: GoogleDaApi | null
}

/* ---------------------------------------------------------------- agendas -- */

export type AgendaInfo = {
  id: AgendaId
  label: string
  /** Nome curto (Select do formulário, badge). */
  short: string
  description: string
  icon: string
  /** Cor do marcador — NUNCA o único sinal: vai junto com ícone e nome. */
  color: string
  tone: 'primary' | 'success' | 'warning' | 'neutral' | 'info'
  derived: boolean
}

/** As quatro agendas do protótipo; as cores vêm de `calendar.css` (claro/escuro). */
export const AGENDAS: readonly AgendaInfo[] = [
  {
    id: 'pessoal',
    label: 'Minha agenda',
    short: 'Minha agenda',
    description: 'Seus eventos e os eventos para os quais você foi convidado.',
    icon: 'fas fa-user',
    color: 'var(--cal-agenda-pessoal)',
    tone: 'primary',
    derived: false,
  },
  {
    id: 'empresa',
    label: 'Corporativo',
    short: 'Corporativo',
    description: 'Eventos da empresa — todo o workspace vê.',
    icon: 'fas fa-building',
    color: 'var(--cal-agenda-empresa)',
    tone: 'warning',
    derived: false,
  },
  {
    id: 'datas',
    label: 'Datas comemorativas e campanhas',
    short: 'Datas comemorativas',
    description: 'Feriados, datas comemorativas e campanhas — todo o workspace vê.',
    icon: 'fas fa-gift',
    color: 'var(--cal-agenda-datas)',
    tone: 'neutral',
    derived: false,
  },
  {
    id: 'itens',
    label: 'Itens de Trabalho',
    short: 'Itens de Trabalho',
    description: 'Derivada: o início e a entrega de cada Item de Trabalho.',
    icon: 'fas fa-tasks',
    color: 'var(--cal-agenda-itens)',
    tone: 'info',
    derived: true,
  },
  {
    id: 'google',
    label: 'Google Calendar',
    short: 'Google Calendar',
    description: 'Os eventos da sua agenda principal do Google — só para você.',
    icon: 'fab fa-google',
    color: 'var(--cal-agenda-google)',
    tone: 'info',
    derived: true,
  },
]

export const AGENDA_IDS: readonly AgendaId[] = AGENDAS.map((a) => a.id)
export const AGENDAS_CRIAVEIS = AGENDAS.filter((a) => !a.derived) as readonly (AgendaInfo & { id: AgendaDeEvento })[]

export const agendaInfo = (id: AgendaId): AgendaInfo => AGENDAS.find((a) => a.id === id) ?? AGENDAS[0]!

/* ------------------------------------------------------------------ datas -- */

const DIA_RE = /^\d{4}-\d{2}-\d{2}$/
export const HORA_RE = /^([01]\d|2[0-3]):[0-5]\d$/

const pad = (n: number) => String(n).padStart(2, '0')

/** `AAAA-MM-DD` da data LOCAL. */
export const dayKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** Lê `AAAA-MM-DD` como data LOCAL (`new Date('2026-08-01')` seria UTC). */
export const parseDayKey = (key: string) => new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)))

const utc = (key: string) => Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)))
const deUtc = (ms: number) => {
  const d = new Date(ms)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** Chave válida e que existe no calendário (`2026-02-30` não passa). */
export const ehDiaValido = (key: string | null | undefined): key is string =>
  typeof key === 'string' && DIA_RE.test(key) && deUtc(utc(key)) === key

export const somarDias = (key: string, dias: number) => deUtc(utc(key) + dias * 86_400_000)

export const diasEntre = (de: string, ate: string) => Math.round((utc(ate) - utc(de)) / 86_400_000)

/** 0 = domingo — a grade começa no domingo, como o protótipo. */
export const diaDaSemana = (key: string) => new Date(utc(key)).getUTCDay()

export const inicioDaSemana = (key: string) => somarDias(key, -diaDaSemana(key))

export const primeiroDoMes = (key: string) => `${key.slice(0, 7)}-01`

/** Mesmo dia `delta` meses adiante; dia 31 num mês de 30 vira o último dia. */
export function somarMeses(key: string, delta: number): string {
  const ano = Number(key.slice(0, 4))
  const mes = Number(key.slice(5, 7)) - 1 + delta
  const alvo = new Date(Date.UTC(ano, mes, 1))
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate()
  return `${alvo.getUTCFullYear()}-${pad(alvo.getUTCMonth() + 1)}-${pad(Math.min(Number(key.slice(8, 10)), ultimo))}`
}

export const ultimoDoMes = (key: string) => somarDias(primeiroDoMes(somarMeses(primeiroDoMes(key), 1)), -1)

/** Dias de `de` a `ate`, inclusive. */
export function diasDoPeriodo(de: string, ate: string): string[] {
  const total = diasEntre(de, ate)
  return Array.from({ length: Math.max(0, total + 1) }, (_, i) => somarDias(de, i))
}

/** Grade fixa de 6 semanas (42 dias) do mês: a altura do mês não muda ao navegar. */
export function gradeDoMes(key: string): string[] {
  const inicio = inicioDaSemana(primeiroDoMes(key))
  return Array.from({ length: 42 }, (_, i) => somarDias(inicio, i))
}

export type Exibicao = 'month' | 'week' | 'day' | 'agenda'

/**
 * O período que a tela pede à API para a exibição — também a chave do
 * `useQuery`. Mês: as 6 semanas da grade; Semana: domingo a sábado; Dia: o
 * dia; Agenda: o mês corrente e o próximo.
 */
export function periodoVisivel(exibicao: Exibicao, selecionado: string): { de: string; ate: string } {
  switch (exibicao) {
    case 'month': {
      const grade = gradeDoMes(selecionado)
      return { de: grade[0]!, ate: grade[41]! }
    }
    case 'week': {
      const de = inicioDaSemana(selecionado)
      return { de, ate: somarDias(de, 6) }
    }
    case 'day':
      return { de: selecionado, ate: selecionado }
    case 'agenda': {
      const de = primeiroDoMes(selecionado)
      return { de, ate: ultimoDoMes(somarMeses(de, 1)) }
    }
  }
}

/** Período que o cabeçalho conta ("N compromissos"): no mês, só o mês — não a grade. */
export function periodoDaContagem(exibicao: Exibicao, selecionado: string): { de: string; ate: string } {
  if (exibicao === 'month') return { de: primeiroDoMes(selecionado), ate: ultimoDoMes(selecionado) }
  return periodoVisivel(exibicao, selecionado)
}

/** Para onde ‹ › levam: um mês (Mês, Agenda), uma semana ou um dia. */
export function navegar(exibicao: Exibicao, selecionado: string, delta: -1 | 1, hoje: string): string {
  if (exibicao === 'week') return somarDias(selecionado, 7 * delta)
  if (exibicao === 'day') return somarDias(selecionado, delta)
  const alvo = primeiroDoMes(somarMeses(primeiroDoMes(selecionado), delta))
  // voltando ao mês de hoje, o dia selecionado é hoje
  return alvo === primeiroDoMes(hoje) ? hoje : alvo
}

/* ---------------------------------------------------------- compromissos -- */

export const ultimoDia = (c: Pick<Compromisso, 'date' | 'endDate'>) =>
  c.endDate !== null && c.endDate > c.date ? c.endDate : c.date

export const ehVariosDias = (c: Pick<Compromisso, 'date' | 'endDate'>) => ultimoDia(c) !== c.date

export const ehDiaInteiro = (c: Pick<Compromisso, 'startTime'>) => c.startTime === null

export const intersectaPeriodo = (c: Pick<Compromisso, 'date' | 'endDate'>, de: string, ate: string) =>
  c.date <= ate && ultimoDia(c) >= de

/** Os dias em que o compromisso aparece, recortados ao período (quando dado). */
export function diasDoCompromisso(c: Pick<Compromisso, 'date' | 'endDate'>, de?: string, ate?: string): string[] {
  const inicio = de !== undefined && de > c.date ? de : c.date
  const fim = ate !== undefined && ate < ultimoDia(c) ? ate : ultimoDia(c)
  return inicio > fim ? [] : diasDoPeriodo(inicio, fim)
}

export function eventoParaCompromisso(e: EventoDaApi): Compromisso {
  return {
    id: e.id,
    agenda: e.agenda,
    title: e.title,
    date: e.date,
    endDate: e.endDate,
    startTime: e.startTime,
    endTime: e.startTime === null ? null : e.endTime,
    location: e.location,
    ownerMemberId: e.ownerMemberId,
    memberIds: e.memberIds,
    evento: e,
    item: null,
  }
}

/**
 * Item de Trabalho → "Início · <título>" e "Entrega · <título>", de dia
 * inteiro, na agenda Itens de Trabalho (como o protótipo).
 */
export function itemParaCompromissos(item: ItemDaApi): Compromisso[] {
  const marco = (marco: 'inicio' | 'entrega', date: string): Compromisso => ({
    id: `item:${item.id}:${marco}`,
    agenda: 'itens',
    title: `${marco === 'inicio' ? 'Início' : 'Entrega'} · ${item.title}`,
    date,
    endDate: null,
    startTime: null,
    endTime: null,
    location: '',
    ownerMemberId: item.assigneeMemberId,
    memberIds: [],
    evento: null,
    item: { ...item, marco },
  })
  const lista: Compromisso[] = []
  if (item.startDate && ehDiaValido(item.startDate)) lista.push(marco('inicio', item.startDate))
  if (item.dueDate && ehDiaValido(item.dueDate)) lista.push(marco('entrega', item.dueDate))
  return lista
}

/** Evento do Google → compromisso da agenda "Google Calendar", de quem conectou. */
export function googleParaCompromisso(g: GoogleDaApi, eu: string): Compromisso {
  return {
    id: `google:${g.id}`,
    agenda: 'google',
    title: g.title,
    date: g.date,
    endDate: g.endDate,
    startTime: g.startTime,
    endTime: g.startTime === null ? null : g.endTime,
    location: g.location,
    ownerMemberId: eu,
    memberIds: [],
    evento: null,
    item: null,
    google: g,
  }
}

export function montarCompromissos(
  dados: { eventos: readonly EventoDaApi[]; itens: readonly ItemDaApi[]; google?: readonly GoogleDaApi[] },
  eu = '',
): Compromisso[] {
  return [
    ...dados.eventos.map(eventoParaCompromisso),
    ...dados.itens.flatMap(itemParaCompromissos),
    ...(dados.google ?? []).map((g) => googleParaCompromisso(g, eu)),
  ]
}

/** Dia inteiro e vários dias primeiro; depois pela hora; depois pelo título. */
export function compararCompromissos(a: Compromisso, b: Compromisso): number {
  const faixaA = ehDiaInteiro(a) || ehVariosDias(a) ? 0 : 1
  const faixaB = ehDiaInteiro(b) || ehVariosDias(b) ? 0 : 1
  if (faixaA !== faixaB) return faixaA - faixaB
  const horaA = a.startTime ?? ''
  const horaB = b.startTime ?? ''
  if (horaA !== horaB) return horaA < horaB ? -1 : 1
  return a.title.localeCompare(b.title, 'pt-BR')
}

/** Índice dia → compromissos (evento de vários dias entra em cada dia), já ordenado. */
export function indexarPorDia(lista: readonly Compromisso[], de?: string, ate?: string): Map<string, Compromisso[]> {
  const indice = new Map<string, Compromisso[]>()
  for (const c of lista) {
    for (const dia of diasDoCompromisso(c, de, ate)) {
      const balde = indice.get(dia)
      if (balde) balde.push(c)
      else indice.set(dia, [c])
    }
  }
  for (const balde of indice.values()) balde.sort(compararCompromissos)
  return indice
}

/**
 * Hora curta do chip num dia: "09:00", "…" nos dias de continuação de um
 * evento de vários dias, ou vazio no dia inteiro (o title do chip já diz
 * "Dia inteiro"; o rótulo "dia" solto ao lado do título lia como ruído).
 */
export function horaNoDia(c: Compromisso, dia: string): string {
  if (ehVariosDias(c) && dia !== c.date) return '…'
  return c.startTime ?? ''
}

/** "09:00 — 10:00", "09:00" ou "Dia inteiro". */
export function faixaDeHorario(c: Pick<Compromisso, 'startTime' | 'endTime'>): string {
  if (c.startTime === null) return 'Dia inteiro'
  return c.endTime === null ? c.startTime : `${c.startTime} — ${c.endTime}`
}

/* ------------------------------------------------------- grade de horas -- */

const minutos = (hora: string) => Number(hora.slice(0, 2)) * 60 + Number(hora.slice(3, 5))

/** Altura mínima na grade (o evento de 5 minutos precisa caber o título). */
export const DURACAO_MINIMA_NA_GRADE = 30

/**
 * Início e fim, em minutos desde 00:00, do evento com hora na grade.
 * Sem fim = 1h; nunca passa da meia-noite.
 */
export function posicaoNaGrade(startTime: string, endTime: string | null): { inicio: number; fim: number } {
  const inicio = minutos(startTime)
  const fimInformado = endTime !== null && HORA_RE.test(endTime) ? minutos(endTime) : inicio + 60
  const fim = Math.min(24 * 60, Math.max(fimInformado, inicio + DURACAO_MINIMA_NA_GRADE))
  return { inicio, fim: Math.max(fim, inicio) }
}

/**
 * Fica na grade de horas o evento com hora de um dia só; dia inteiro e
 * vários dias vão para a faixa "Dia inteiro" do topo (em todos os dias do
 * intervalo).
 */
export const vaiParaGradeDeHoras = (c: Compromisso) => !ehDiaInteiro(c) && !ehVariosDias(c)

export type NaGrade<T> = { item: T; inicio: number; fim: number; coluna: number; colunas: number }

/**
 * Lado a lado quando se sobrepõem: cada grupo de eventos encavalados divide
 * a largura do dia em `colunas`, e cada evento ocupa a primeira coluna livre.
 */
export function distribuirNaGrade<T>(itens: readonly { item: T; inicio: number; fim: number }[]): NaGrade<T>[] {
  const ordenados = [...itens].sort((a, b) => a.inicio - b.inicio || b.fim - a.fim)
  const saida: NaGrade<T>[] = []
  let grupo: NaGrade<T>[] = []
  let fimDoGrupo = -1
  let fimDasColunas: number[] = []

  const fecharGrupo = () => {
    for (const g of grupo) g.colunas = fimDasColunas.length
    saida.push(...grupo)
    grupo = []
    fimDasColunas = []
  }

  for (const atual of ordenados) {
    if (grupo.length > 0 && atual.inicio >= fimDoGrupo) fecharGrupo()
    let coluna = fimDasColunas.findIndex((fim) => fim <= atual.inicio)
    if (coluna === -1) {
      coluna = fimDasColunas.length
      fimDasColunas.push(atual.fim)
    } else fimDasColunas[coluna] = atual.fim
    grupo.push({ ...atual, coluna, colunas: 1 })
    fimDoGrupo = grupo.length === 1 ? atual.fim : Math.max(fimDoGrupo, atual.fim)
  }
  if (grupo.length > 0) fecharGrupo()
  return saida
}

/* ---------------------------------------------------------------- filtros -- */

/** Seção "Visão" do explorer: QUEM aparece. */
export type Audiencia = 'me' | 'all' | 'custom'

export type Filtros = {
  audiencia: Audiencia
  /** memberId da sessão. */
  eu: string
  /** Pessoas escolhidas em "Selecionar pessoas". */
  pessoas: readonly string[]
  agendas: readonly AgendaId[]
  termo: string
}

const normalizar = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt-BR')

/**
 * "Somente eu": o que criei ou para o que fui convidado + Corporativo + Datas
 * + itens em que sou responsável. "Todo mundo": tudo o que a API devolveu.
 * "Selecionar pessoas": dono ou convidado entre as escolhidas + Corporativo +
 * Datas + itens cujo responsável está entre as escolhidas.
 */
export function passaNaAudiencia(c: Compromisso, f: Pick<Filtros, 'audiencia' | 'eu' | 'pessoas'>): boolean {
  if (f.audiencia === 'all') return true
  if (c.agenda === 'empresa' || c.agenda === 'datas') return true
  const quem = f.audiencia === 'me' ? [f.eu] : f.pessoas
  if (c.ownerMemberId !== null && quem.includes(c.ownerMemberId)) return true
  return c.memberIds.some((id) => quem.includes(id))
}

/**
 * Dica ao lado de "Pessoas" no explorer (SPEC-20261027-2350-calendario-real, ajuste
 * "marcar todos / desmarcar todos"): em "Selecionar pessoas" diz quantas estão
 * marcadas — com nenhuma, só Corporativo e Datas comemorativas aparecem.
 */
export function dicaDasPessoas(audiencia: Audiencia, marcadas: number, total: number): string {
  if (audiencia === 'all') return 'todas visíveis'
  if (audiencia === 'me') return 'ocultas em “Somente eu”'
  if (marcadas === 0) return 'nenhuma marcada'
  return `${marcadas} de ${total}`
}

export function casaBusca(c: Compromisso, termo: string): boolean {
  const t = normalizar(termo.trim())
  if (t === '') return true
  const campos = [c.title, c.location, agendaInfo(c.agenda).label, c.item?.code ?? '']
  return normalizar(campos.join(' ')).includes(t)
}

export function filtrarCompromissos(lista: readonly Compromisso[], f: Filtros): Compromisso[] {
  return lista.filter((c) => f.agendas.includes(c.agenda) && passaNaAudiencia(c, f) && casaBusca(c, f.termo))
}

/* ------------------------------------------------------------- formulário -- */

export type FormDeEvento = {
  title: string
  agenda: AgendaDeEvento
  date: Date | null
  /** Término em outro dia — nulo = no mesmo dia. */
  endDate: Date | null
  diaInteiro: boolean
  startTime: string
  endTime: string
  location: string
  meetingUrl: string
  description: string
  memberIds: string[]
  /** "Enviar também para o meu Google Calendar". */
  googleSync: boolean
}

const umaHoraDepois = (hora: string) => {
  const fim = minutos(hora) + 60
  return fim >= 24 * 60 ? '' : `${pad(Math.floor(fim / 60))}:${pad(fim % 60)}`
}

/** Novo evento no dia (e na hora, quando veio de um horário da grade). */
export function formVazio(date: Date, hora: string | null = null): FormDeEvento {
  const comHora = hora !== null && HORA_RE.test(hora)
  return {
    title: '',
    agenda: 'pessoal',
    date,
    endDate: null,
    diaInteiro: !comHora,
    startTime: comHora ? hora : '',
    endTime: comHora ? umaHoraDepois(hora) : '',
    location: '',
    meetingUrl: '',
    description: '',
    memberIds: [],
    googleSync: false,
  }
}

export function formDoEvento(e: EventoDaApi): FormDeEvento {
  return {
    title: e.title,
    agenda: e.agenda,
    date: parseDayKey(e.date),
    endDate: e.endDate !== null && e.endDate !== e.date ? parseDayKey(e.endDate) : null,
    diaInteiro: e.startTime === null,
    startTime: e.startTime ?? '',
    endTime: e.endTime ?? '',
    location: e.location,
    meetingUrl: e.meetingUrl ?? '',
    description: e.description,
    memberIds: [...e.memberIds],
    googleSync: e.googleSync ?? false,
  }
}

export type ErrosDoForm = Partial<Record<'title' | 'date' | 'endDate' | 'startTime' | 'endTime' | 'meetingUrl', string>>

/** As mesmas regras da API (`regras.ts`), para o erro aparecer no campo. */
export function errosDoForm(f: FormDeEvento): ErrosDoForm {
  const erros: ErrosDoForm = {}
  if (f.title.trim() === '') erros.title = 'Dê um título ao evento.'
  if (f.date === null) erros.date = 'Escolha a data do evento.'
  if (f.date !== null && f.endDate !== null && dayKey(f.endDate) < dayKey(f.date)) {
    erros.endDate = 'O término não pode ser antes do início.'
  }
  if (!f.diaInteiro) {
    const inicio = f.startTime.trim()
    const fim = f.endTime.trim()
    if (inicio === '') erros.startTime = 'Informe a hora de início, ou marque "Dia inteiro".'
    else if (!HORA_RE.test(inicio)) erros.startTime = 'Use o formato HH:mm.'
    if (fim !== '' && !HORA_RE.test(fim)) erros.endTime = 'Use o formato HH:mm.'
    const mesmoDia = f.endDate === null || (f.date !== null && dayKey(f.endDate) === dayKey(f.date))
    if (!erros.startTime && !erros.endTime && fim !== '' && mesmoDia && fim < inicio) {
      erros.endTime = 'A hora de término não pode ser antes da de início.'
    }
  }
  const link = f.meetingUrl.trim()
  if (link !== '' && !/^https?:\/\//i.test(link)) erros.meetingUrl = 'O link precisa começar com http:// ou https://.'
  return erros
}

export const formValido = (f: FormDeEvento) => Object.keys(errosDoForm(f)).length === 0

/** Corpo do POST/PUT. Só chame com o formulário válido (data preenchida). */
export function corpoDoForm(f: FormDeEvento): CorpoDoEvento {
  const date = f.date ? dayKey(f.date) : ''
  const endDate = f.endDate ? dayKey(f.endDate) : null
  const startTime = f.diaInteiro ? null : f.startTime.trim()
  const fim = f.endTime.trim()
  return {
    agenda: f.agenda,
    title: f.title.trim(),
    description: f.description,
    location: f.location.trim(),
    meetingUrl: f.meetingUrl.trim(),
    date,
    endDate: endDate !== null && endDate !== date ? endDate : null,
    startTime,
    endTime: startTime === null || fim === '' ? null : fim,
    memberIds: [...new Set(f.memberIds)],
    googleSync: f.googleSync,
  }
}

/* --------------------------------------- design do calendário (2026-10-31) -- */

/** Ponto ou retângulo de onde um popover nasce (o elemento clicado, ou o ponteiro). */
export type Ancora = { left: number; top: number; right: number; bottom: number }

/** Margem mínima do popover às bordas da janela. */
const MARGEM_DA_JANELA = 12

/**
 * Onde o popover fica: à direita do que foi clicado; sem espaço, à esquerda;
 * sem espaço nos dois lados, alinhado a ele. No eixo vertical, começa na
 * altura do clique e sobe o necessário para caber — como no design.
 */
export function posicaoDoPopover(
  ancora: Ancora,
  tamanho: { largura: number; altura: number },
  janela: { largura: number; altura: number },
): { x: number; y: number } {
  const { largura, altura } = tamanho
  let x = ancora.right + 10
  if (x + largura > janela.largura - MARGEM_DA_JANELA) x = ancora.left - largura - 10
  if (x < MARGEM_DA_JANELA) {
    x = Math.max(MARGEM_DA_JANELA, Math.min(janela.largura - largura - MARGEM_DA_JANELA, ancora.left))
  }
  const y = Math.max(MARGEM_DA_JANELA, Math.min(ancora.top - 8, janela.altura - altura - MARGEM_DA_JANELA))
  return { x: Math.round(x), y: Math.round(y) }
}

/** Situação de um marco de Item de Trabalho — sempre com texto, nunca só cor. */
export type SituacaoDoItem = { texto: 'Concluído' | 'Atrasado' | 'Planejado' | 'Em andamento'; tom: 'success' | 'danger' | 'info' | 'warning' }

export function situacaoDoItem(item: { concluido: boolean; marco: 'inicio' | 'entrega' }, dia: string, hoje: string): SituacaoDoItem {
  if (item.concluido) return { texto: 'Concluído', tom: 'success' }
  if (item.marco === 'entrega' && dia < hoje) return { texto: 'Atrasado', tom: 'danger' }
  if (dia > hoje) return { texto: 'Planejado', tom: 'info' }
  return { texto: 'Em andamento', tom: 'warning' }
}

/**
 * Quantos chips cabem numa célula do mês com `altura` px: com "+N mais"
 * reservado (`comMais`) e sem ele — o design mostra todos quando cabem e,
 * quando não, um a menos para dar lugar ao "+N mais".
 */
export function chipsQueCabem(altura: number): { todos: number; comMais: number } {
  const CABECA = 34
  const CHIP = 22
  const MAIS = 20
  return {
    todos: Math.max(0, Math.floor((altura - CABECA) / CHIP)),
    comMais: Math.max(0, Math.floor((altura - CABECA - MAIS) / CHIP)),
  }
}
